import type { DecorationPlatform } from '@xjlv2/api-client'
import type {
  RenDecorationComponentDefinition,
  RenDecorationDataSourceRef,
  RenDecorationFieldDefinition,
  RenDecorationPaletteItem
} from '../../types'

export type DecorationPaletteGroup = {
  key: string
  label: string
  scope: 'COMMON' | 'BUSINESS'
  ownerModuleCode: string
  items: RenDecorationPaletteItem[]
}

export type DecorationDataSourceSchema = {
  sourceRefs: RenDecorationDataSourceRef[]
  bindingFields: RenDecorationFieldDefinition[]
}

function parseArray<T>(value: string | undefined): T[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value) as unknown
    return Array.isArray(parsed) ? parsed as T[] : []
  } catch {
    return []
  }
}

function parseRecord(value: string): Record<string, unknown> {
  if (!value) return {}
  try {
    const parsed = JSON.parse(value) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {}
  } catch {
    return {}
  }
}

function scopeOf(definition: RenDecorationComponentDefinition): 'COMMON' | 'BUSINESS' {
  return definition.componentScope?.toUpperCase() === 'BUSINESS' ? 'BUSINESS' : 'COMMON'
}

function platformsOf(definition: RenDecorationComponentDefinition): string[] {
  return parseArray<string>(definition.supportedPlatformsJson).map((item) => String(item).toUpperCase())
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function parseDataSourceSchema(raw: string | undefined): DecorationDataSourceSchema {
  const sourceRefs: RenDecorationDataSourceRef[] = []
  const bindingFields: RenDecorationFieldDefinition[] = []
  for (const item of parseArray<unknown>(raw)) {
    if (!isObjectRecord(item)) continue
    const code = typeof item.code === 'string' ? item.code : ''
    if (!code) continue
    if (item.type === 'binding') {
      bindingFields.push({
        code,
        label: typeof item.label === 'string' ? item.label : code,
        type: 'binding'
      })
      continue
    }
    if (typeof item.moduleCode === 'string') {
      sourceRefs.push({ code, moduleCode: item.moduleCode })
    }
  }
  return { sourceRefs, bindingFields }
}

export function hasComponentDataSourceTab(schema: DecorationDataSourceSchema): boolean {
  return schema.sourceRefs.length > 0 || schema.bindingFields.length > 0
}

export function availableDataSourcesFor<T extends { sourceCode: string }>(
  schema: DecorationDataSourceSchema,
  moduleSources: T[]
): T[] {
  if (!hasComponentDataSourceTab(schema)) return []
  if (schema.sourceRefs.length === 0) return moduleSources
  const codes = new Set(schema.sourceRefs.map((item) => item.code))
  return moduleSources.filter((source) => codes.has(source.sourceCode))
}

function paletteItemOf(definition: RenDecorationComponentDefinition): RenDecorationPaletteItem {
  const supportedPlatforms = platformsOf(definition)
  const dataSourceSchema = parseDataSourceSchema(definition.dataSourceSchemaJson)
  return {
    type: definition.type,
    label: definition.label,
    description: definition.description,
    group: definition.groupName,
    icon: definition.icon,
    defaults: parseRecord(definition.defaultPropsJson),
    propertyFields: parseArray<RenDecorationFieldDefinition>(definition.propertySchemaJson),
    styleFields: parseArray<RenDecorationFieldDefinition>(definition.styleSchemaJson),
    dataSources: dataSourceSchema.sourceRefs,
    dataBindingFields: dataSourceSchema.bindingFields,
    componentScope: scopeOf(definition),
    ownerModuleCode: definition.ownerModuleCode || '',
    supportedPlatforms,
    supportedPlatformsJson: definition.supportedPlatformsJson,
    protocolVersion: definition.protocolVersion
  }
}

export function groupDecorationPalette(
  definitions: RenDecorationComponentDefinition[],
  keyword: string,
  platform: DecorationPlatform
): DecorationPaletteGroup[] {
  const normalizedKeyword = keyword.trim().toLocaleLowerCase()
  const grouped = new Map<string, DecorationPaletteGroup>()

  definitions
    .filter((definition) => definition.enabled !== false)
    .filter((definition) => {
      const supportedPlatforms = platformsOf(definition)
      return supportedPlatforms.length === 0 || supportedPlatforms.includes(platform)
    })
    .filter((definition) => !normalizedKeyword || [
      definition.type,
      definition.label,
      definition.description,
      definition.groupName,
      definition.ownerModuleCode
    ].some((value) => String(value || '').toLocaleLowerCase().includes(normalizedKeyword)))
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .forEach((definition) => {
      const scope = scopeOf(definition)
      const ownerModuleCode = definition.ownerModuleCode || (scope === 'COMMON' ? 'platform' : 'unknown')
      const groupName = definition.groupName || ownerModuleCode
      const key = scope === 'COMMON' ? 'COMMON' : `BUSINESS:${ownerModuleCode}:${groupName}`
      const group = grouped.get(key) ?? {
        key,
        label: scope === 'COMMON' ? '通用组件' : groupName,
        scope,
        ownerModuleCode,
        items: []
      }
      group.items.push(paletteItemOf(definition))
      grouped.set(key, group)
    })

  return Array.from(grouped.values()).sort((left, right) => {
    if (left.scope !== right.scope) return left.scope === 'COMMON' ? -1 : 1
    return left.label.localeCompare(right.label)
  })
}

export function editorSupportedTypes(definitions: RenDecorationComponentDefinition[]): Set<string> {
  return new Set(definitions.filter((definition) => definition.enabled !== false).map((definition) => definition.type))
}
